import React from 'react'
import { Trans, t } from '@lingui/macro'
import dayjs, { OpUnitType, DurationUnitType } from 'shared/dayjs'
import { getDateFormatBasedOnLocale } from '../common/date-time-pickers/dateTimePickersUtils'
import { DateFilterType } from '../common/dateFilter'

export interface PredefinedDateRangeItem {
  value: number
  amount: number
  unitTranslated: string
  unit: DurationUnitType
  label: JSX.Element
}

/**
 * used for analytics and dashboards filtering
 */
export const predefinedDateRanges: PredefinedDateRangeItem[] = [
  {
    value: 1,
    amount: 7,
    unit: 'days',
    unitTranslated: t`days`,
    label: <Trans>Last 7 days</Trans>
  },
  {
    value: 2,
    amount: 28,
    unit: 'days',
    unitTranslated: t`days`,
    label: <Trans>Last 28 days</Trans>
  },
  {
    value: 3,
    amount: 3,
    unit: 'months',
    unitTranslated: t`months`,
    label: <Trans>Last 3 months</Trans>
  },
  {
    value: 4,
    amount: 6,
    unit: 'months',
    unitTranslated: t`months`,
    label: <Trans>Last 6 months</Trans>
  },
  {
    value: 5,
    amount: 12,
    unit: 'months',
    unitTranslated: t`months`,
    label: <Trans>Last 12 months</Trans>
  }
]

export const defaultDateRange = predefinedDateRanges[1]

export type DateRange = {
  from: Date | string
  to: Date | string
  period?: number | null
}

export const getDateRangeById = (id: number) => {
  return predefinedDateRanges.find((r) => r.value === id) || null
}

export const getPreselectedDateRange = ({
  from,
  to,
  period
}: DateRange): PredefinedDateRangeItem | undefined | null => {
  if (period) {
    return getDateRangeById(period)
  }

  return predefinedDateRanges.find(
    (dateRange) =>
      dayjs(from)
        .add(dateRange.amount, dateRange.unit as OpUnitType)
        .startOf('day')
        .format('L')
        .toString() === dayjs(to).startOf('day').format('L').toString()
  )
}

export const showSelectedRangeOrCustom = ({
  period,
  from,
  to
}: DateFilterType) => {
  const format = getDateFormatBasedOnLocale()

  return (
    period?.label ??
    `${dayjs(from).format(format)} -
              ${dayjs(to).format(format)}`
  )
}

export const getUserRangeTranslation = ({
  selectedDateRangeItem,
  from,
  to
}: {
  selectedDateRangeItem?: PredefinedDateRangeItem | null
  from: Date
  to: Date
}) => {
  const format = getDateFormatBasedOnLocale()
  const startPrefix = selectedDateRangeItem
    ? t`Started in the last `
    : t`Started within `
  const datesEnding = selectedDateRangeItem
    ? t`${selectedDateRangeItem.amount} ${selectedDateRangeItem.unitTranslated}`
    : `${dayjs(from).format(format)} -
              ${dayjs(to).format(format)}`

  return `${startPrefix} ${datesEnding}`
}
